<script lang="ts">
  // Dialog — the modal shell (wiki/feature-design-system.md); the host mounts it while open.
  import type { Snippet } from 'svelte';
  import { IconButton } from '$lib/ui';
  import { X } from '$lib/ui/icons';

  interface Props {
    title: string;
    closeLabel: string;
    onclose: () => void;
    testid?: string;
    /** `sm` fits a one-column settings form; `lg` a 16:9 video. */
    size?: 'lg' | 'md' | 'sm';
    children: Snippet;
    /** Pinned under the scrolling body — the cost line and the actions. */
    footer?: Snippet;
  }

  let { title, closeLabel, onclose, testid, size = 'md', children, footer }: Props = $props();

  const titleId = `dialog-title-${Math.random().toString(36).slice(2)}`;
  let dialogEl = $state<HTMLDialogElement | undefined>(undefined);

  $effect(() => {
    const el = dialogEl;
    if (!el) return;
    el.showModal();
    return () => el.close();
  });

  // The host owns closing; a native close would run ahead of it.
  function onCancel(e: Event): void {
    e.preventDefault();
    onclose();
  }
</script>

<dialog
  bind:this={dialogEl}
  class="vd-dialog vd-dialog--{size}"
  aria-labelledby={titleId}
  oncancel={onCancel}
  onclick={(e) => e.target === dialogEl && onclose()}
  data-testid={testid}
>
  <div class="vd-dialog__panel">
    <header class="vd-dialog__head">
      <h2 id={titleId}>{title}</h2>
      <IconButton icon={X} label={closeLabel} onclick={onclose} data-testid="dialog-close" />
    </header>
    <div class="vd-dialog__body">{@render children()}</div>
    {#if footer}<footer class="vd-dialog__foot">{@render footer()}</footer>{/if}
  </div>
</dialog>

<style lang="scss">
  .vd-dialog {
    border: none;
    padding: 0;
    background: transparent;
    width: min(40rem, calc(100vw - 2rem));
    max-width: none;
    max-height: calc(100dvh - 2rem);
    color: var(--ink);
  }

  .vd-dialog--lg {
    width: min(60rem, calc(100vw - 2rem));
  }

  .vd-dialog--sm {
    width: min(34rem, calc(100vw - 2rem));
  }

  .vd-dialog::backdrop {
    background: color-mix(in srgb, var(--ink) 50%, transparent);
  }

  .vd-dialog__panel {
    display: flex;
    flex-direction: column;
    max-height: calc(100dvh - 2rem);
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 14px;
    box-shadow: 0 24px 60px rgb(0 0 0 / 0.3);
    font-family: var(--font-ui);
  }

  .vd-dialog__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1rem 0.75rem 1.5rem;
    border-bottom: 1px solid var(--line);
  }

  .vd-dialog__head h2 {
    margin: 0;
    font-family: var(--font-hand);
    font-weight: 400;
    font-size: var(--text-guide);
    line-height: 1.25;
  }

  .vd-dialog__body {
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1rem 1.5rem 1.5rem;
  }

  .vd-dialog__foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
    padding: 0.875rem 1.5rem;
    border-top: 1px solid var(--line);
  }

  @media (max-width: 640px) {
    .vd-dialog,
    .vd-dialog__panel {
      width: 100vw;
      height: 100dvh;
      max-height: none;
      margin: 0;
      border: 0;
      border-radius: 0;
    }

    .vd-dialog__head {
      padding: 0.5rem 0.5rem 0.5rem 1rem;
    }

    .vd-dialog__body {
      flex: 1;
      padding: 1rem;
    }

    .vd-dialog__foot {
      padding: 0.75rem 1rem;
    }
  }
</style>
